import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { sendLoginEmail } from '../../utils/auth';
import { Session } from '@supabase/supabase-js';
import { VerificationInput } from './VerificationInput';
import { supabase } from '../../lib/supabase';

interface EmailLoginProps {
  onSuccess?: (session: Session) => void;
}

interface EmailForm {
  email: string;
}

export const EmailLogin: React.FC<EmailLoginProps> = ({ onSuccess }) => {
  const [step, setStep] = useState<'email' | 'verification'>('email');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  const emailForm = useForm<EmailForm>();
  
  const startResendCooldown = () => {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleEmailSubmit = async (data: EmailForm) => {
    console.log('Attempting to send verification email to:', data.email);
    try {
      setLoading(true);
      setError(null);
      await sendLoginEmail(data.email);
      console.log('Verification email sent successfully');
      setEmail(data.email);
      setStep('verification');
      startResendCooldown();
      setVerificationCode('');
    } catch (error) {
      console.error('Failed to send verification email:', error);
      setError('Failed to send verification email. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    
    console.log('Attempting to resend verification code to:', email);
    try {
      setLoading(true);
      setError(null);
      await sendLoginEmail(email);
      console.log('Verification code resent successfully');
      startResendCooldown();
      setVerificationCode('');
    } catch (error) {
      console.error('Failed to resend verification code:', error);
      setError('Failed to resend verification email. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleVerificationComplete = async (code: string) => {
    console.log('Attempting to verify code for:', email);
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token: code,
        type: 'email'
      });

      if (error) {
        throw error;
      }

      console.log('Verification successful, session:', data.session);
      
      if (data.session) {
        // 确保会话被保存
        await supabase.auth.setSession(data.session);
        
        if (onSuccess) {
          onSuccess(data.session);
        }
      } else {
        setError('验证成功但未获取到会话信息');
        setVerificationCode('');
      }
    } catch (error) {
      console.error('Failed to verify code:', error);
      setError('验证码无效，请重试');
      setVerificationCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full space-y-6">
      {step === 'email' ? (
        <form onSubmit={emailForm.handleSubmit(handleEmailSubmit)} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300">
              Email address
            </label>
            <div className="mt-1">
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/10 
                         text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500
                         transition-all duration-300"
                placeholder="Enter your email"
                {...emailForm.register('email', { required: true })}
              />
            </div>
          </div>

          {error && (
            <div className="text-red-400 text-sm">{error}</div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg 
                     bg-indigo-600/80 hover:bg-indigo-600 border border-indigo-500/30 
                     transition-all duration-300 disabled:opacity-50"
          >
            {loading ? 'Sending...' : 'Send Verification Code'}
          </button>
        </form>
      ) : (
        <div className="space-y-4">
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-gray-300">
              Verification Code
            </label>
            <p className="mt-2 mb-4 text-sm text-gray-400">
              Enter the 6-digit code sent to {email}
            </p>
            <VerificationInput
              value={verificationCode}
              onChange={setVerificationCode}
              onComplete={handleVerificationComplete}
              disabled={loading}
            />
          </div>

          {error && (
            <div className="text-red-400 text-sm">{error}</div>
          )}

          <div className="flex flex-col space-y-2">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={resendCooldown > 0 || loading}
              className="text-sm text-indigo-400 hover:text-indigo-300 disabled:text-gray-500
                       transition-colors duration-300"
            >
              {resendCooldown > 0 
                ? `Resend code in ${resendCooldown}s` 
                : 'Resend verification code'}
            </button>

            <button
              type="button"
              onClick={() => {
                setStep('email');
                setError(null);
                emailForm.reset();
              }}
              className="text-sm text-indigo-400 hover:text-indigo-300
                       transition-colors duration-300"
            >
              Use a different email
            </button>
          </div>
        </div>
      )}
    </div>
  );
}; 